import { tap } from 'rxjs/operators';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Component, EventEmitter, Input, Output, inject, input } from '@angular/core';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';

import { ZerosixService } from '@core/services/zerosix/zerosix.service';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { AnalyticsService } from '@core/services/app/analytics.service';

@Component({
  selector: 'app-zerosix-otp-step',
  templateUrl: './zerosix-otp-step.component.html',
  standalone: false,
})
export class ZerosixOtpStepComponent extends DestroyerBase {
  /* -- Injects -- */
  private readonly zerosixService = inject(ZerosixService);
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly analyticsService = inject(AnalyticsService);

  readonly phone = input<string>();

  @Output() onClose = new EventEmitter<Event>();
  @Output() goToPreviousStep = new EventEmitter<void>();
  @Output() goToNextStep = new EventEmitter<void>();

  @Input() actionFieldColor?: ContentBuilderFieldColors;

  form: UntypedFormGroup;
  isValidating = false;
  errorMessage?: string;

  constructor() {
    super();
    this.form = this.formBuilder.group({
      ['authCode']: ['', [Validators.required]],
    });
  }

  onSubmit(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    const authCode = this.form.controls['authCode']?.value;

    if (this.form.invalid || this.isValidating || !authCode || !this.phone()) {
      return;
    }
    this.analyticsService.trackLoyaltyButtonClick('zerosix', 'submit_otp');
    this.isValidating = true;

    this.zerosixService
      .attachUserWithOtp$(authCode)
      .pipe(tap(() => this.onOtpValidated()))
      .subscribe({
        error: error => {
          this.handleErrorResponse(error.error);
        },
        complete: () => (this.isValidating = false),
      });
  }

  onGoToPreviousStep(): void {
    this.goToPreviousStep.next();
  }

  private onOtpValidated() {
    this.goToNextStep.next();
  }

  onKeypadNumberClick(number: number): void {
    const currentValue = this.form.controls.authCode.value;
    this.form.controls.authCode.setValue(currentValue + number.toString());
  }

  onKeypadDeleteClick(): void {
    const currentValue = this.form.controls.authCode.value;
    this.form.controls.authCode.setValue(currentValue.slice(0, -1));
  }

  private handleErrorResponse(_error: any): void {
    this.isValidating = false;
    this.errorMessage = 'zerosix.register.otp.error';
    this.analyticsService.trackErrorDisplay(
      'otp_invalid',
      _error?.code?.toString() ?? 'UNKNOWN',
      this.errorMessage,
      'zerosix_otp_step',
    );
  }
}
